<template>
	<div class="cell configurator-options__wrapper" v-if="showOption">
		<div
			class="configurator-options__item cursor-pointer"
			@click="clickEvent()"
			:class="{
				'configurator-options__item--active' : checked
		}">
			<img
				:src="imagePath"
				class="configurator-options__image"
				width="500" >
			<input
				:id="option.id"
				class="configurator-block__input input-field input-field--custom"
				type="radio"
				name="selectOption"
				:checked="checked"
				:disabled="unmetRequired || notAvailableWithOffset">
			<span class="configurator__input-selectable"/>
			<p class="configurator-block__label cursor-pointer">
				{{ option.name }}
			</p>
			<div class="indicator-container indicator-container--bottom">
				<div
					class="indicator"
					v-if="option.isSustainableChoice">
					<tooltip
						hover
						tooltip-wrapper-class-name="tool-tip__wrapper--sustainable"
						content="Duurzaam product">
						<div class="indicator__icon tool-tip__arrow-hook" v-html="getSvg.icon.leaf" />
					</tooltip>
				</div>
				<div
					class="indicator"
					v-if="hasOffsetAvailable && hasOffsetAvailableCalculated">
					<tooltip
						hover
						tooltip-wrapper-class-name="tool-tip__wrapper--offset"
						content="Offset beschikbaar">
						<div class="indicator__icon tool-tip__arrow-hook" v-html="getSvg.icon.inkDrops" />
					</tooltip>
				</div>
			</div>
			<div class="indicator-container indicator-container--top">
				<span
					v-if="option.isOnlyForTesting"
					class="only-for-testing--label"
					title="Deze optie is alleen zichtbaar voor jou omdat je bent ingelogd met een intern testaccount. Klanten zien deze optie niet.">
					OnlyForTesting
				</span>
				<span v-if="option.isBestChoice" class="best-choice--label">
					meest gekozen
				</span>
				<div
					v-if="option.description && option.description.length > 3"
					class="tool-tip__wrapper grid tool-tip__wrapper--absolute tool-tip__wrapper--multi">
					<div
						class="tool-tip__wrapper-inner"
						@mouseenter="descriptionActive = true"
						@mouseleave="descriptionActive = false">
						<transition name="fade">
							<div class="tool-tip" v-show="descriptionActive">
								<span>{{ option.description }}</span>
							</div>
						</transition>
						<span class="tool-tip__label tool-tip__label--absolute" title="Info">i</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { EventBus } from "@/store/index.js";
import tooltip from "@/components/element/tooltip";
import { Events, pushEvent } from "@/helpers/tracking.js";

export default {
	name: "OptionItem",
	components: { tooltip },
	props: {
		option: {
			type: Object,
			required: true
		},
		group: {
			type: Object,
			required: true
		}
	},
	data () {
		return {
			descriptionActive: false
		};
	},
	computed: {
		imageClass () {
			let imageClass = "";
			imageClass += this.group.rounded
				? "configurator-options__shape--circle"
				: "configurator-options__shape--square";
			return imageClass;
		},
		showImage () {
			return this.group.image;
		},
		showOption () {
			if (this.groupIsLocked && !this.checked) {
				return false;
			}
			if (this.printSidesLocked && !this.checked) {
				return false;
			}
			return !(this.unmetRequired && this.getUnavailableOptions.length >= 6);
		},
		parent () {
			return this.group;
		},
		selectedOptions () {
			return this.$store.getters.getSelectedOptions;
		},
		imagePath () {
			return this.option.iconPath;
		},
		selectedOptionIds () {
			return this.$store.getters.getSelectedOptionIds;
		},
		printSidesCountChoice () {
			return this.$store.getters.isPrintSidesCountChoice;
		},
		requiredOptionsWithinGroups () {
			return this.$store.getters.getRequiredOptionsWithinGroups;
		},
		printSidesAvailability () {
			return this.$store.getters.getPrintSidesAvailability;
		},
		coverPrintSidesAvailability () {
			return this.$store.getters.getCoverPrintSidesAvailability;
		},
		existingConfigurationFinalized () {
			return this.$store.getters.existingConfigIsFinalized;
		},
		uploadsComplete () {
			return this.$store.getters.uploadsAreComplete;
		},
		dictionary () {
			return this.$store.getters.getDictionary;
		},
		deferredOption () {
			const isVisible = this.group.visibleOptionIds.some(o => o.id === this.option.id);
			return !isVisible;
		},
		groupIsLocked () {
			return this.$store.getters.getLockedOptionGroupIds.includes(this.group.id);
		},
		notAvailableWithOffset () {
			return this.getOrderLine.isOffset && !this.hasOffsetAvailable;
		},
		printSidesLocked () {
			// Once a PDF has been uploaded (or an existing configuration with an upload is
			// reopened), the number of sides is derived from the uploaded file and must not
			// be changeable anymore - the customer already committed to it when uploading.
			if ((this.uploadsComplete || this.existingConfigurationFinalized) && this.dictionary.optionCategory[this.group.type] === "print") {
				// if has upload and type is print
				if (this.printSidesCountChoice && this.$store.getters.getFile.pageCount !== this.option.printSideCount) {
					// if isPrintSidesCountChoice (read store~index.js) && sides to print dont equal the pagecount. The option needs to be unmet.
					return this.$store.getters.getFile.pageCount !== null; // because it does need a pagecount to be able to check.
				}

				if (this.dictionary.chunkType[this.group.chunkType] === "content" && this.printSidesAvailability.set) {
					// if has upload and type is print and chunktype is content
					if (!this.printSidesAvailability.single && this.option.printSideCount === 1) {
						// if singlesided print is not possible (based on upload) and current iteration of option selects singlesided print
						return true;
					}

					if (!this.printSidesAvailability.double && this.option.printSideCount === 2) {
						// if doublesided print is not possible (based on upload) and current iteration of option selects doublesided print
						return true;
					}
				}

				if (this.dictionary.chunkType[this.group.chunkType] === "cover" && this.coverPrintSidesAvailability.set) {
					// if has upload and type is print and chunktype is cover
					if (!this.coverPrintSidesAvailability.single && this.option.printSideCount === 1) {
						// if singlesided print is not possible for the cover (based on upload) and current iteration of option selects singlesided print
						return true;
					}

					if (!this.coverPrintSidesAvailability.double && this.option.printSideCount === 2) {
						// if doublesided print is not possible for the cover (based on upload) and current iteration of option selects doublesided print
						return true;
					}
				}
			}

			return false;
		},
		unmetRequired () {
			if (this.printSidesLocked) {
				return true;
			}

			if (this.getAvailableOptions.includes(this.option.id)) {
				return false;
			}
			if (this.getUnavailableOptions.includes(this.option.id)) {
				return true;
			}
		},
		itemClass () {
			const itemClass = [];
			if (!this.isOffTheShelf) {
				itemClass.push("configurator-options__item--list");
			}

			if (this.deferredOption) {
				itemClass.push("configurator-options__item--deferred");
			}

			if (this.checked) {
				itemClass.push("configurator-options__item--active");
			}

			if ((this.unmetRequired && this.getUnavailableOptions.length < 6) || this.notAvailableWithOffset) {
				itemClass.push("configurator-options__item--unmet-requirements");
			}
			return itemClass;
		},
		checked () {
			let checked = false;

			for (let i in this.selectedOptions) {
				if (this.selectedOptions[i].id === this.option.id) {
					checked = true;
				}
			}
			return checked;
		},
		getOrderLine () {
			return this.$store.getters.getOrderLine;
		},
		hasOffsetAvailable () {
			return this.option.hasOffsetPrices;
		},
		hasOffsetAvailableCalculated () {
			// check based on option id, in groups, of available offsetprices with previousely selected options
			const relevantOptionIds = [];
			const toSelect = this.option.baseOptionId;
			const groups = this.$store.getters.getUnsortedSelectedGroups;

			const relevantOptionGroups = [];
			Object.keys(groups).forEach(key => {
				if (groups[key].id !== this.$store.getters.getCurrentStep.data.groupId) {
					if (groups[key].optionCategory > 0) {
						relevantOptionGroups.push(groups[key]);
					}
				}
			});

			this.$store.getters.getUnsortedSelectedOptions.forEach(option => {
				relevantOptionGroups.forEach(group => {
					if (group.id === option.parentGroupId) {
						relevantOptionIds.push(option.baseOptionId);
					}
				});
			});

			relevantOptionIds.push(toSelect);

			const availableOffsetPricings = this.$store.getters.getSelectedFormat.offsetPricings.find(pricings => {
				if (relevantOptionIds.every(id => pricings.optionIds.includes(id))) {
					return pricings;
				}
			});

			return availableOffsetPricings;
		},
		getSvg () {
			return this.$store.getters.getSvg;
		},
		getAvailableOptions () {
			return this.$store.getters.getAvailableOptions;
		},
		getUnavailableOptions () {
			return this.$store.getters.getUnavailableOptions;
		},
		isOffTheShelf () {
			return this.$store.getters.isOffTheShelf;
		}
	},
	methods: {
		clickEvent () {
			if (this.unmetRequired) {
				return;
			}

			this.selectOption(this.parent.id, this.option);
		},
		setImage () {
			this.$emit("setSelectedOptionImage", this.imagePath);
		},
		selectOption (parentId, option) {
			option.parentGroupId = parentId;
			this.$store.commit("SET_SELECTED_OPTION", option);
			this.setImage();

			pushEvent(Events.SELECT_OPTION, {
				optionId: option.id,
				optionName: option.name,
				groupId: parentId,
				groupName: this.parent.name
			});
		}
	},
	watch: {
		unmetRequired: function (a, b) {
			if (a === true && this.checked && this.uploadsComplete) {
				this.$store.commit("CLEAR_SELECTED_OPTION", this.group.id);
				this.$store.commit("SET_STEP_ACTIVE", this.group.id);

				if (this.printSidesCountChoice) {
					EventBus.$emit("configuratorModal", {
						title: "Bedrukking niet mogelijk",
						content: `<p>De door u gekozen bedrukking is niet mogelijk met het geüploade bestand</p>`
					});
				} else {
					const printTypeString = !this.printSidesAvailability.single
						? "enkelzijdig"
						: !this.printSidesAvailability.double
							? "dubbelzijdig"
							: null;

					EventBus.$emit("configuratorModal", {
						title: `Bedrukking ${printTypeString} niet mogelijk`,
						content: `<p>De door u gekozen bedrukking(${printTypeString}) is niet mogelijk met het geüploade bestand</p>`
					});
				}
			}
		}
	}
};
</script>

<style lang="scss">
.configurator-options__info-label {
  background: #7dc8ff;
  position: absolute;
  right: 5%;
  top: 5%;
  width: 30px;
  height: 30px;
  border-radius: 100%;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 1.8rem;
  font-weight: 700;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.2);

  &:hover {
	background: #a1d9ff;
  }
}

.configurator-options-wrapper
  .configurator-options__item--active
  .input-field__selectable:after {
  content: "";
  position: absolute;
  display: block !important;
  width: 25px !important;
  height: 25px !important;
  border-radius: 100%;
  background: #e73467;
  top: -2px;
  left: -2px;
}

.configurator-options-wrapper
  .configurator-options__item--active
  .input-field__selectable:before {
  content: "";
  position: absolute;
  display: block !important;
  width: 7px !important;
  height: 7px !important;
  border-radius: 100%;
  background: #f0f0f0;
  z-index: 100;
  top: 7px;
  left: 7px;
}

.configurator-options__item--unmet-requirements .input-field__selectable {
  cursor: default;

  &:after {
	display: none !important;
  }
}
.not-available-with-offset {
	font-weight: 600;
	font-size: 1rem;
	text-transform: uppercase;
	color: #ccc;
}

</style>



// WEBPACK FOOTER //
// src/components/option/option-item.vue